/**
 * @module HypothesisCard
 * @purpose Full hypothesis card — accent bar, header meta, no-score banner, stepped walkthrough, footer.
 * @depends HypothesisWalkthrough, HypothesisScopeFunnel, HypothesisMatchedObservables,
 *   HypothesisReferenceMatches, HypothesisCoLocatedFiles, HypothesisTimeline, HypothesisAbsenceList,
 *   HypothesisExplanationBlock, HypothesisDeferredSection, HypothesisCorrelationsSection,
 *   types/reasoning, react-i18next
 * @dependedOnBy HypothesesView
 *
 * @rules
 * - Performance: pure display — all data via props, no data fetching
 * - State: none (walkthrough state owned by HypothesisWalkthrough)
 * - Errors: n/a — purely presentational; evidence arrays default to [] when absent
 */

import { useTranslation } from 'react-i18next'
import type { ContextEnvelope, Hypothesis, SignalCounts } from '../../../../../types/reasoning'
import { HypothesisDeferredSection } from './HypothesisDeferredSection'
import { HypothesisExplanationBlock } from './HypothesisExplanationBlock'
import { HypothesisAbsenceList } from './HypothesisAbsenceList'
import { HypothesisMatchedObservables } from './HypothesisMatchedObservables'
import { HypothesisReferenceMatches } from './HypothesisReferenceMatches'
import { HypothesisScopeFunnel } from './HypothesisScopeFunnel'
import { HypothesisCoLocatedFiles } from './HypothesisCoLocatedFiles'
import { HypothesisTimeline } from './HypothesisTimeline'
import { HypothesisCorrelationsSection } from './HypothesisCorrelationsSection'
import { HypothesisWalkthrough } from './HypothesisWalkthrough'
import type { WalkthroughStep } from './HypothesisWalkthrough'

interface HypothesisCardProps {
  hypothesis: Hypothesis
  signalCounts: SignalCounts
  totalObservations: number
  onViewBriefing?: () => void
}

interface CardHeaderMetaProps {
  hypothesis: Hypothesis
}

interface CardFooterProps {
  onViewBriefing: (() => void) | undefined
}

const CARD_CONTAINER: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  overflow: 'hidden',
}

const ACCENT_BAR: React.CSSProperties = {
  height: '4px',
  background: 'var(--accent)',
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

const NO_SCORE_BANNER: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderLeft: '3px solid var(--accent)',
  padding: '8px 12px',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.7rem',
  color: 'var(--text-dim)',
  margin: '12px 0',
}

const FOOTER_STYLE: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  padding: '12px 0 0',
  marginTop: '8px',
}

const DISCLAIMER_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  marginBottom: '8px',
}

const BTN_ACTIVE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  padding: '4px 10px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
  cursor: 'pointer',
}

const BTN_DISABLED: React.CSSProperties = {
  ...BTN_ACTIVE,
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  cursor: 'not-allowed',
}

const META_TEXT: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
}

function CardHeaderMeta({ hypothesis }: CardHeaderMetaProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const roleLabel = hypothesis.role === 'source'
    ? t('reasoning.hypothesis.card.roleSource')
    : hypothesis.role === 'target'
      ? t('reasoning.hypothesis.card.roleTarget')
      : t('reasoning.hypothesis.card.roleUnspecified')
  const endpointDisplay = hypothesis.endpointId ?? t('reasoning.hypothesis.card.endpointNone')

  return (
    <div style={{ padding: '12px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: 'var(--text)', marginBottom: '6px', fontWeight: 600 }}>
        {hypothesis.predicateName}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
        <span style={CHIP_BASE}>{t('reasoning.hypothesis.card.versionLabel')}: v{hypothesis.predicateVersion}</span>
        <span style={{ ...CHIP_BASE, background: 'var(--accent)', color: 'var(--btn-primary-text, #fff)', border: 'none' }}>
          {roleLabel}
        </span>
        <span style={META_TEXT}>
          {t('reasoning.hypothesis.card.endpointLabel')}: {endpointDisplay}
        </span>
        <span style={META_TEXT}>
          {t('reasoning.hypothesis.card.evaluatedLabel')}: {hypothesis.updatedAt}
        </span>
      </div>
    </div>
  )
}

function CardFooter({ onViewBriefing }: CardFooterProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div style={FOOTER_STYLE}>
      <div style={DISCLAIMER_STYLE}>{t('reasoning.hypothesis.card.footerDisclaimer')}</div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          disabled
          aria-disabled={true}
          title={t('reasoning.hypothesis.card.openInCanvasDisabled')}
          style={BTN_DISABLED}
        >
          {t('reasoning.hypothesis.card.openInCanvas')}
        </button>
        {onViewBriefing !== undefined && (
          <button type="button" onClick={onViewBriefing} style={BTN_ACTIVE}>
            {t('reasoning.hypothesis.card.viewBriefing')}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Full hypothesis card with accent bar, header meta, no-score banner, stepped walkthrough, footer.
 *
 * @behaviour Assembles the eight walkthrough steps from existing sub-components and new envelope
 *   sections. Open-in-Canvas is a disabled stub (cross-tab drillToCluster wiring deferred).
 *   Reference matches (step 3) is a dedicated step — parity with Jeff's reference-card step 2,
 *   not folded into Matched observables (ADV-4). Correlations renders real network/lineage/temporal
 *   correlations followed by the deferred actor-identity block (actor identity from 4688 is
 *   genuinely unavailable in v1).
 * @errors n/a — evidence arrays default to [] when absent; contextEnvelope defaults to empty.
 */
export function HypothesisCard({ hypothesis, signalCounts, totalObservations, onViewBriefing }: HypothesisCardProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const presentEntries = hypothesis.evidence?.present ?? []
  const absentEntries = hypothesis.evidence?.expected_but_absent ?? []
  const referenceMatchEntries = hypothesis.evidence?.reference_matches ?? []
  // Degrade gracefully when the API omits the envelope (e.g. an older backend, or a
  // hypothesis persisted before this feature): the sections render their neutral
  // empty states rather than crashing the whole card.
  const envelope: ContextEnvelope = hypothesis.contextEnvelope ?? {
    clusterRef: null,
    correlatedEvidence: [],
    timeline: [],
    timelineWindow: null,
  }

  const steps: WalkthroughStep[] = [
    {
      title: t('reasoning.hypothesis.scopeFunnel.title'),
      body: <HypothesisScopeFunnel signalCounts={signalCounts} totalObservations={totalObservations} />,
    },
    {
      title: t('reasoning.hypothesis.matchedObservables.title'),
      body: <HypothesisMatchedObservables entries={presentEntries} />,
    },
    {
      title: t('reasoning.hypothesis.referenceMatches.title'),
      body: <HypothesisReferenceMatches entries={referenceMatchEntries} />,
    },
    {
      title: t('reasoning.hypothesis.coLocatedFiles.title'),
      body: <HypothesisCoLocatedFiles entries={envelope.correlatedEvidence} />,
    },
    {
      title: t('reasoning.hypothesis.timeline.title'),
      body: <HypothesisTimeline events={envelope.timeline} window={envelope.timelineWindow} />,
    },
    {
      title: t('reasoning.hypothesis.correlations.title'),
      body: (
        <>
          <HypothesisCorrelationsSection correlatedEvidence={envelope.correlatedEvidence} />
          <HypothesisDeferredSection
            title={t('reasoning.hypothesis.deferred.actorIdentity.title')}
            body={t('reasoning.hypothesis.deferred.actorIdentity.body')}
          />
        </>
      ),
    },
    {
      title: t('reasoning.hypothesis.absenceList.title'),
      body: <HypothesisAbsenceList entries={absentEntries} />,
    },
    {
      title: t('reasoning.hypothesis.explanation.title'),
      body: <HypothesisExplanationBlock explanation={hypothesis.explanation} />,
    },
  ]

  return (
    <div style={CARD_CONTAINER}>
      <div style={ACCENT_BAR} />
      <CardHeaderMeta hypothesis={hypothesis} />
      <div style={{ padding: '0 12px 12px' }}>
        <div style={NO_SCORE_BANNER} role="note">
          {t('reasoning.hypothesis.card.noScoreBanner')}
        </div>
        <HypothesisWalkthrough steps={steps} />
        <CardFooter onViewBriefing={onViewBriefing} />
      </div>
    </div>
  )
}
